// All widget CSS lives here as a string so it can be injected into the
// shadow root. No external stylesheets — the widget runs isolated from the
// host page's styles regardless of CSS framework or reset in use.
//
// Theme tokens are exposed as CSS custom properties on :host. The mount
// helper writes per-client overrides directly onto the host element, and
// they inherit into the shadow root the standard CSS-variable way.
//
// CSS comments inside this template literal MUST use /* ... */. Using //
// silently kills the line for the browser parser even though TS lets it
// through — see the widget-lessons memory for the bug story.

export const styles = `
:host {
  --ai-accent: #3b82f6;
  --ai-accent-fg: #ffffff;
  --ai-bg-surface: #f3f4f6;
  --ai-text: #1f2937;
  --ai-text-muted: #6b7280;
  --ai-border: #e5e7eb;
  /* Optional per-element overrides, deliberately NOT defined here:
     --ai-user-bubble-bg (falls back to --ai-accent)
     --ai-card-action-fg (falls back per surface: accent text on the card
       footer link, accent-fg on the contact-card pills)
     --ai-send-icon      (falls back to --ai-accent-fg)
     Each usage site carries its own var() fallback so an unset token is
     pixel-identical to the pre-token behavior. */

  all: initial;
  /* "all: initial" resets display to "inline", which would make the host
     ignore explicit height. Force display:block so a host with
     style="height:600px" is honored. position:relative establishes a
     containing block for the absolutely-positioned shadow container
     (see mount.ts) — that bypasses the height:100% percentage chain
     entirely, which is fragile when the host lives inside parent CSS
     (Elementor's off-canvas widget, for one) that overrides heights. */
  display: block;
  width: 100%;
  /* Fallback minimum height for hosts that don't declare one. Page builders
     (Elementor's HTML element, etc.) often render an empty target div with
     no implicit height, and the inner container uses position:absolute +
     inset:0 to fill the host -- so a zero-height host collapses to zero
     height. Widget would render but be invisible. :host has low specificity
     so any explicit height on the host (inline style, parent CSS, theme
     rule) still wins. */
  min-height: 500px;
  position: relative;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-size: 14px;
  line-height: 1.5;
  color: var(--ai-text);
}

*, *::before, *::after { box-sizing: border-box; }

button {
  font: inherit;
  color: inherit;
  cursor: pointer;
  border: none;
  background: none;
  padding: 0;
}

textarea {
  font: inherit;
  color: inherit;
  border: none;
  outline: none;
  resize: none;
}

a { color: var(--ai-accent); text-decoration: none; }
a:hover { text-decoration: underline; }

/* === Chat panel === */
/* No card chrome (border / shadow / radius) — the panel reads as a
   continuous part of the surrounding page content, not a discrete chat
   box. The page's own heading carries the brand name, so the widget has
   no header of its own. */
.panel {
  width: 100%;
  height: 100%;
  position: relative;
  /* No min-height: the panel must be free to shrink to the host height that
     mount.ts computes from the available viewport space, so the input bar
     stays on screen even when the widget sits low on a tall page. The
     internal messages area absorbs the squeeze via its own overflow. */
  /* Upper bound on panel height. mount.ts sizes the host element to the
     space actually available below the widget's top edge (so the input bar
     stays visible without scrolling the host page) and clamps it to this
     same 720px ceiling — the panel then fills the host with no gap. This
     max-height is the JS-disabled fallback and the hard ceiling on large
     screens; it must match FIT_MAX_PX in mount.ts. */
  max-height: 720px;
  background: transparent;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Welcome state: collapse the panel to its content so the input sits
   right under "See suggested searches" instead of being pushed to the
   bottom of a 480px-tall empty surface. Once a real message lands,
   the "empty" modifier class falls away and the panel re-claims height. */
.panel.empty {
  height: auto;
  min-height: 0;
}
.panel.empty .messages { flex: 0 0 auto; }

/* === Messages area === */
.messages {
  flex: 1 1 0;
  /* min-height: 0 is critical. Flex items default to min-height: auto
     (= content height), which prevents the messages area from shrinking
     below its content size. Without this override, a long conversation
     pushes the panel taller than its parent and the input bar disappears
     off the bottom of the page. */
  min-height: 0;
  overflow-y: auto;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  scroll-behavior: smooth;
}
/* When scrollIntoView({block:"start"}) targets a user message, leave a
   small visual gap above the bubble so the top doesn't kiss the top
   edge of the chat area. */
.message.user { scroll-margin-top: 12px; }
/* On phones the post-submit scroll uses scrollIntoView, which moves the host
   page; offset further (~50px more) so the question clears the host's sticky
   header instead of hiding behind it. Larger screens use the contained scroll
   and are unaffected by scroll-margin. */
@media (pointer: coarse) and (max-width: 768px) {
  .message.user { scroll-margin-top: 62px; }
}
.messages::-webkit-scrollbar { width: 6px; }
.messages::-webkit-scrollbar-thumb {
  background: var(--ai-border);
  border-radius: 3px;
}

/* === Welcome state === */
.welcome {
  text-align: left;
  padding: 0 0 20px;
  color: var(--ai-text-muted);
  animation: fade-in 220ms ease-out;
}
.welcome-text {
  color: #374151;
  font-size: 14px;
  margin: 0 0 16px;
}
.starter-prompts {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: stretch;
  margin-top: 8px;
}
.starter-prompt {
  text-align: left;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--ai-bg-surface);
  border: 1px solid var(--ai-border);
  color: #374151;
  font-size: 13px;
  line-height: 1.4;
  transition: filter 0.12s, transform 0.12s, border-color 0.12s;
}
.starter-prompt:hover {
  filter: brightness(0.97);
  border-color: color-mix(in srgb, var(--ai-accent) 25%, var(--ai-border));
}
.starter-prompt:active { transform: scale(0.99); }

.starter-toggle {
  margin-top: 4px;
  font-size: 12px;
  color: var(--ai-accent);
  background: transparent;
  padding: 4px 0;
  transition: filter 0.12s;
}
.starter-toggle:hover {
  text-decoration: underline;
  filter: brightness(0.95);
}

/* === Message bubbles === */
.message {
  animation: message-in 200ms cubic-bezier(0.16, 1, 0.3, 1);
  display: flex;
  flex-direction: column;
}
.message.user { align-items: flex-end; }
.message.assistant { align-items: flex-start; }

@keyframes message-in {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

.bubble {
  max-width: 86%;
  padding: 10px 14px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-wrap: break-word;
}
.message.user .bubble {
  background: var(--ai-user-bubble-bg, var(--ai-accent));
  color: var(--ai-accent-fg);
  border-bottom-right-radius: 4px;
}
.message.assistant .bubble {
  background: var(--ai-bg-surface);
  color: #111827;
  border-bottom-left-radius: 4px;
}

/* === Source chip list below assistant messages === */
/* Stacked vertically; one row per unique source URL. Each row shows the
   full source title; wrapping if long, no ellipsis. */
.sources {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 10px;
  max-width: 100%;
  align-self: stretch;
}
.source-chip {
  display: block;
  padding: 8px 12px;
  background: white;
  border: 1px solid var(--ai-border);
  border-left: 3px solid color-mix(in srgb, var(--ai-accent) 60%, var(--ai-border));
  border-radius: 8px;
  font-size: 12px;
  color: #4b5563;
  transition: background 0.12s, border-color 0.12s, color 0.12s, transform 0.12s;
  text-decoration: none !important;
  cursor: pointer;
}
.source-chip:hover {
  background: var(--ai-bg-surface);
  border-color: color-mix(in srgb, var(--ai-accent) 30%, var(--ai-border));
  border-left-color: var(--ai-accent);
  color: var(--ai-text);
  transform: translateY(-1px);
}
.source-chip-title {
  line-height: 1.4;
  word-wrap: break-word;
  overflow-wrap: anywhere;
}

/* === Card list (clickable catalog recommendations) === */
/* Rendered between the assistant bubble and the source chips when the
   LLM emits [[card:type:slug]] markers that resolve to a known card.
   Generic shape — thumbnail + title + key/value pills — so the same
   component works for plans, products, events, etc. */
.card-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
  max-width: 100%;
  align-self: stretch;
  /* Query container so cards go 2-up based on the actual available width,
     not the viewport (the widget can be embedded narrow on a desktop page). */
  container-type: inline-size;
}
.card {
  display: flex;
  flex-direction: column;
  /* Default: one per row (narrow panels / mobile). */
  flex: 1 1 100%;
  min-width: 0;
  background: white;
  border: 1px solid var(--ai-border);
  border-left: 3px solid color-mix(in srgb, var(--ai-accent) 60%, var(--ai-border));
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.12s, transform 0.12s;
}
/* When the card area is wide enough, lay cards out two per row (~50% each)
   so wide cards stop leaving a big empty gap on the right. */
@container (min-width: 460px) {
  .card {
    flex: 1 1 calc(50% - 4px);
    max-width: calc(50% - 4px);
  }
}
.card:hover {
  border-color: color-mix(in srgb, var(--ai-accent) 30%, var(--ai-border));
  border-left-color: var(--ai-accent);
  transform: translateY(-1px);
}
.card-link {
  display: flex;
  gap: 12px;
  align-items: stretch;
  padding: 10px;
  text-decoration: none !important;
  color: var(--ai-text);
  cursor: pointer;
  transition: background 0.12s;
}
.card-link:hover {
  background: var(--ai-bg-surface);
}
.card-brochure {
  display: block;
  padding: 7px 12px;
  border-top: 1px solid var(--ai-border);
  background: var(--ai-bg-surface);
  font-size: 12px;
  font-weight: 600;
  color: var(--ai-card-action-fg, var(--ai-accent));
  text-decoration: none !important;
  cursor: pointer;
}
.card-brochure:hover {
  text-decoration: underline !important;
}
.card-thumb {
  width: 96px;
  height: 72px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
  background: var(--ai-bg-surface);
}
.card-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  flex: 1;
}
.card-title {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  line-height: 1.3;
  word-wrap: break-word;
}
.card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.card-pill {
  display: inline-block;
  font-size: 11px;
  line-height: 1.3;
  padding: 2px 8px;
  background: var(--ai-bg-surface);
  color: #4b5563;
  border-radius: 10px;
}
.card-pill-label {
  color: var(--ai-text-muted);
}

/* === Rep contact cards === */
.rep-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
  align-self: stretch;
}
.rep-card {
  border: 1px solid var(--ai-border);
  border-left: 3px solid color-mix(in srgb, var(--ai-accent) 60%, var(--ai-border));
  border-radius: 8px;
  padding: 10px;
  background: white;
}
.rep-card-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
}
.rep-card-name {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.rep-card-sub {
  font-size: 12px;
  color: var(--ai-text-muted);
}
.rep-card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}
.rep-card-action {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 10px;
  border-radius: 8px;
  background: var(--ai-accent);
  color: var(--ai-card-action-fg, var(--ai-accent-fg, #fff));
  text-decoration: none !important;
  cursor: pointer;
}
.rep-card-action:hover {
  filter: brightness(0.95);
}

/* === Typing dots === */
.typing {
  display: inline-flex;
  gap: 4px;
  align-items: center;
  padding: 2px 0;
}
.typing-dot {
  width: 6px;
  height: 6px;
  border-radius: 3px;
  background: #9ca3af;
  display: inline-block;
  animation: typing-pulse 1.4s infinite ease-in-out both;
}
.typing-dot:nth-child(2) { animation-delay: 0.18s; }
.typing-dot:nth-child(3) { animation-delay: 0.36s; }

@keyframes typing-pulse {
  0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
  40% { opacity: 1; transform: scale(1); }
}

/* === Input area === */
.input {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 12px 0;
  border-top: 1px solid var(--ai-border);
  background: transparent;
  flex-shrink: 0;
}
.input-wrap {
  flex: 1;
  position: relative;
  display: flex;
  min-width: 0;
}
.input-textarea {
  flex: 1;
  width: 100%;
  min-height: 36px;
  max-height: 120px;
  padding: 8px 12px;
  background: var(--ai-bg-surface);
  border: 1px solid var(--ai-border);
  border-radius: 18px;
  font-size: 14px;
  line-height: 1.4;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.input-counter {
  position: absolute;
  right: 10px;
  bottom: 6px;
  font-size: 11px;
  color: var(--ai-text-muted);
  pointer-events: none;
  background: rgba(255, 255, 255, 0.9);
  padding: 1px 6px;
  border-radius: 4px;
  font-variant-numeric: tabular-nums;
}
.input-counter.at-limit { color: #c2410c; }
.input-textarea:focus {
  background: white;
  border-color: color-mix(in srgb, var(--ai-accent) 60%, var(--ai-border));
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--ai-accent) 18%, transparent);
}
.input-textarea:disabled {
  background: var(--ai-bg-surface);
  color: var(--ai-text-muted);
  cursor: not-allowed;
}
.input-send {
  width: 36px;
  height: 36px;
  border-radius: 18px;
  background: var(--ai-accent);
  color: var(--ai-send-icon, var(--ai-accent-fg));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: filter 0.12s, transform 0.12s, opacity 0.12s, background 0.12s;
}
.input-send:hover:not(:disabled) {
  filter: brightness(1.08);
  transform: scale(1.06);
}
.input-send:active:not(:disabled) { transform: scale(0.96); }
.input-send:disabled {
  background: #d1d5db;
  cursor: not-allowed;
  filter: none;
}

/* === Email transcript === */
.email-bar {
  padding: 8px 0 4px;
  text-align: left;
  flex-shrink: 0;
}
.email-open {
  font-size: 12px;
  color: var(--ai-text-muted);
  text-decoration: underline;
  text-underline-offset: 2px;
}
.email-open:hover { color: var(--ai-text); }
/* Open form floats as a bottom-anchored panel inside the widget, capped to
   the widget height and internally scrollable — so the Send button is always
   reachable even when the embed is short or the host page's footer sits right
   under the widget (it used to overflow in-flow and get clipped). */
.email-form {
  position: absolute;
  left: 10px;
  right: 10px;
  bottom: 10px;
  z-index: 30;
  max-height: calc(100% - 20px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  background: var(--ai-bg-surface);
  border: 1px solid var(--ai-border);
  border-radius: 10px;
  box-shadow: 0 6px 22px rgba(0, 0, 0, 0.16);
}
.email-actions {
  position: sticky;
  bottom: 0;
  background: var(--ai-bg-surface);
  padding-top: 6px;
}
.email-field { display: flex; flex-direction: column; gap: 4px; }
.email-label { font-size: 11px; color: var(--ai-text-muted); }
.email-input {
  font: inherit;
  font-size: 13px;
  color: var(--ai-text);
  background: white;
  border: 1px solid var(--ai-border);
  border-radius: 6px;
  padding: 7px 10px;
}
.email-input:focus { outline: none; border-color: var(--ai-accent); }
.email-input:disabled { opacity: 0.6; }
/* iOS auto-zooms the page when a focused text input has font-size < 16px.
   Bump the inputs to 16px on touch devices (where that zoom happens) while
   keeping the tighter sizes for pointer/desktop. */
@media (pointer: coarse) {
  .input-textarea,
  .email-input { font-size: 16px; }
}
.email-cc {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--ai-text);
}
.email-cc input { width: 14px; height: 14px; }
.email-actions { display: flex; gap: 8px; }
.email-send {
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  color: var(--ai-accent-fg);
  background: var(--ai-accent);
  border-radius: 6px;
  padding: 7px 14px;
}
.email-send:hover:not(:disabled) { filter: brightness(1.05); }
.email-send:disabled { opacity: 0.5; cursor: default; }
.email-cancel {
  font: inherit;
  font-size: 13px;
  color: var(--ai-text-muted);
  background: transparent;
  border: 1px solid var(--ai-border);
  border-radius: 6px;
  padding: 6px 12px;
}
.email-cancel:hover:not(:disabled) { color: var(--ai-text); }
.email-status {
  font-size: 12px;
  margin-top: 2px;
}
.email-status--error { color: #b91c1c; }
.email-status--ok { color: #047857; }

/* === Footer === */
.footer {
  padding: 8px 0 0;
  text-align: left;
  font-size: 11px;
  color: #9ca3af;
  background: transparent;
  flex-shrink: 0;
}
.footer a { color: var(--ai-text-muted); }
.footer a:hover { color: #4b5563; }

/* === Terms & Conditions modal === */
/* Renders in place of the messages + input area when the user hasn't yet
   accepted the terms. The brand header above and footer below remain
   visible so the visitor sees what they're agreeing to. */
.terms-host {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 0;
  background: transparent;
  animation: fade-in 200ms ease-out;
}
.terms {
  max-width: 560px;
}
.terms-title {
  font-weight: 600;
  font-size: 15px;
  color: #111827;
  margin-bottom: 10px;
}
.terms-summary {
  margin: 0 0 14px;
  padding: 0 0 0 18px;
  color: #374151;
  font-size: 13px;
  line-height: 1.55;
}
.terms-summary li { margin: 0 0 6px; }
.terms-expand {
  font-size: 12px;
  color: var(--ai-accent);
  background: transparent;
  padding: 4px 0;
  margin-bottom: 8px;
  text-decoration: underline;
}
.terms-expand:hover { filter: brightness(0.92); }
.terms-body {
  font-size: 12px;
  line-height: 1.55;
  color: #4b5563;
  background: var(--ai-bg-surface);
  border: 1px solid var(--ai-border);
  border-radius: 8px;
  padding: 12px 14px;
  max-height: 240px;
  overflow-y: auto;
  margin-bottom: 14px;
}
.terms-body p { margin: 0 0 10px; }
.terms-body p:last-child { margin-bottom: 0; }
.terms-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  padding: 6px 0 12px;
  user-select: none;
}
.terms-checkbox input {
  margin-top: 2px;
  cursor: pointer;
  accent-color: var(--ai-accent);
}
.terms-accept {
  display: block;
  width: 100%;
  padding: 10px 14px;
  background: var(--ai-accent);
  color: var(--ai-accent-fg);
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  transition: filter 0.12s, opacity 0.12s;
}
.terms-accept:hover:not(:disabled) { filter: brightness(1.05); }
.terms-accept:disabled {
  background: #d1d5db;
  cursor: not-allowed;
}
`;
